import React, {useEffect, useMemo, useState, useCallback, useContext} from "react";
import {GoogleMap, Marker, OverlayView, Circle, useLoadScript} from "@react-google-maps/api";
import { GOOGLE_MAPS_CONFIG } from '../../../../constants/GoogleMapsConfig';
import GoogleMapsStyle from "../../../../GoogleMapStyle";
import { MaterialIcons } from '@expo/vector-icons';
import CampTypes, { getCampTypeColor, getCampTypeIconName } from "../../../../data/CampTypes";
import CampMapPopup from "./CampMapPopup";
import {Pressable, Text} from "react-native";
import SearchOps from "../../../../data/SearchOps";
import GeoUtils from "../../../../utility/GeoUtils";
import {GlobalStateContext} from "../../../../components/general/GlobalStateProvider";


const CampsMapView = ({camps, setSelectedCamp, onMapCampsUpdate}) => {
    const fallbackPosition = {lat: 42.3744, lng: -71.1169}; // Harvard University
    const SEARCH_RADIUS_MILES = 20;
    
    const {isLoaded, loadError} = useLoadScript(GOOGLE_MAPS_CONFIG);
    const { globalMapCenter, setGlobalMapCenter, globalLocation, setGlobalLocation, globalSearch, setGlobalSearch } = useContext(GlobalStateContext);
    
    // Initialize with cached map center if available, otherwise use fallback
    const initialCenter = globalMapCenter || (globalLocation ? {
        lat: globalLocation.coords.latitude,
        lng: globalLocation.coords.longitude
    } : fallbackPosition);
    
    const [selectedCampId, setSelectedCampId] = useState<string | null>(null);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [mapZoom, setMapZoom] = useState(10);
    const [mapCenter, setMapCenter] = useState(initialCenter);
    const [userLocation, setUserLocation] = useState<{lat: number, lng: number} | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const lastSearchCenterRef = React.useRef<{lat: number, lng: number} | null>(null);
    const initialSearchDoneRef = React.useRef(false);
    const [isLoadingUserLocation, setIsLoadingUserLocation] = useState(false);
    // Initialize lastAppliedGlobalCenterRef with the current globalMapCenter to avoid re-triggering
    const lastAppliedGlobalCenterRef = React.useRef<{lat: number; lng: number} | null>(globalMapCenter);

    const validCamps = useMemo(() => {
        return (camps || []).filter(
            (c) => {
                // Check if using new geolocation structure
                if (c?.geolocation?.lat && c?.geolocation?.lng) {
                    return typeof c.geolocation.lat === 'number' && 
                           typeof c.geolocation.lng === 'number' && 
                           !isNaN(c.geolocation.lat) && 
                           !isNaN(c.geolocation.lng);
                }
                // Fallback to old structure
                return typeof c?.lat === 'number' && 
                       typeof c?.lng === 'number' && 
                       !isNaN(c.lat) && 
                       !isNaN(c.lng);
            }
        ).map(c => {
            // Normalize to always use lat/lng at top level for map display
            if (c.geolocation?.lat && c.geolocation?.lng) {
                return { ...c, lat: c.geolocation.lat, lng: c.geolocation.lng };
            }
            return c;
        });
    }, [camps]);

    // Search for camps based on map center
    const searchCampsInMapArea = useCallback(async (center: {lat: number, lng: number}, force: boolean = false) => {
        if (!onMapCampsUpdate) return;
        
        // Check if already searching
        if (isSearching) {
            return;
        }
        
        // Check if center has moved significantly (at least 5 miles from last search)
        if (!force && lastSearchCenterRef.current) {
            const distance = GeoUtils.calculateDistance(
                lastSearchCenterRef.current.lat,
                lastSearchCenterRef.current.lng,
                center.lat,
                center.lng,
                true
            );
            
            // Only search if moved more than 5 miles
            if (distance < 5) {
                return;
            }
        }
        
        setIsSearching(true);
        lastSearchCenterRef.current = center;
        
        try {
            // Format center as address for SearchOps (it will geocode cache it)
            const addressString = `${center.lat},${center.lng}`;
            
            const results = await SearchOps.searchCamps({
                location: addressString,
                maxDistance: SEARCH_RADIUS_MILES,
            });
            
            onMapCampsUpdate(results);
        } catch (error) {
            console.error('Error searching camps in map area:', error);
        } finally {
            setIsSearching(false);
        }
    }, [onMapCampsUpdate, isSearching, SEARCH_RADIUS_MILES]);

    // If currently selected camp is no longer in the set, clear selection
    useEffect(() => {
        if (selectedCampId && !validCamps.some(c => c.campId === selectedCampId)) {
            setSelectedCampId(null);
        }
    }, [validCamps, selectedCampId]);

    // Center map on selected camp
    useEffect(() => {
        if (selectedCampId) {
            const selectedCamp = validCamps.find(c => c.campId === selectedCampId);
            if (selectedCamp) {
                // Update the map center state to the actual camp location
                setMapCenter({ lat: selectedCamp.lat, lng: selectedCamp.lng });
                setMapZoom(12);
            }
        }
    }, [selectedCampId, validCamps]);

    // Listen for zoom changes and map drag
    useEffect(() => {
        if (map) {
            const zoomListener = map.addListener('zoom_changed', () => {
                setMapZoom(map.getZoom() || 10);
            });
            
            // Listen for when user stops dragging the map (debounced)
            const dragEndListener = map.addListener('dragend', () => {
                // Clear any pending search
                if (searchTimeoutRef.current) {
                    clearTimeout(searchTimeoutRef.current);
                }
                
                // Wait 1 second after drag ends before searching
                searchTimeoutRef.current = setTimeout(() => {
                    const center = map.getCenter();
                    if (center) {
                        const newCenter = { lat: center.lat(), lng: center.lng() };
                        setMapCenter(newCenter);
                        
                        // Update global map center when user drags map (NOT the physical location)
                        setGlobalMapCenter(newCenter);
                        
                        // Search for camps in new map area
                        searchCampsInMapArea(newCenter);
                    }
                }, 1000);
            });
            
            return () => {
                google.maps.event.removeListener(zoomListener);
                google.maps.event.removeListener(dragEndListener);
                if (searchTimeoutRef.current) {
                    clearTimeout(searchTimeoutRef.current);
                }
            };
        }
    }, [map, searchCampsInMapArea]);

    // Respond to external/global center updates ONCE per new value
    useEffect(() => {
        if (!globalMapCenter || !GeoUtils.isValidCoordinates(globalMapCenter.lat, globalMapCenter.lng)) return;

        const last = lastAppliedGlobalCenterRef.current;
        const isSameAsLast = last && last.lat === globalMapCenter.lat && last.lng === globalMapCenter.lng;
        if (isSameAsLast) return;

        lastAppliedGlobalCenterRef.current = { lat: globalMapCenter.lat, lng: globalMapCenter.lng };

        setMapCenter(globalMapCenter);
        setMapZoom(12);
        // Trigger search around the new center
        searchCampsInMapArea(globalMapCenter, true);
        initialSearchDoneRef.current = true; // prevent geolocation-based initial search
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalMapCenter]);

    // Keep userLocation in sync with physical globalLocation (never tied to map center)
    useEffect(() => {
        if (globalLocation?.coords) {
            setUserLocation({
                lat: globalLocation.coords.latitude,
                lng: globalLocation.coords.longitude,
            });
        }
    }, [globalLocation?.coords]);

    // If a "Nearby" search just ran (address cleared) ensure the map recenters to physical location
    useEffect(() => {
        if (globalSearch && globalSearch.address === '' && globalLocation?.coords) {
            const newCenter = { lat: globalLocation.coords.latitude, lng: globalLocation.coords.longitude };
            const last = lastAppliedGlobalCenterRef.current;
            const isSame = last && last.lat === newCenter.lat && last.lng === newCenter.lng;
            if (!isSame) {
                lastAppliedGlobalCenterRef.current = newCenter;
                setMapCenter(newCenter);
                setMapZoom(12);
                setGlobalMapCenter(newCenter);
                // Trigger a fresh search at this center
                searchCampsInMapArea(newCenter, true);
                initialSearchDoneRef.current = true;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalSearch?.address, globalLocation?.coords]);

    // Get user's current location on component mount (only once)
    useEffect(() => {
        if (initialSearchDoneRef.current) return;
        
        const getUserLocation = () => {
            // Check if we have a cached map center first (highest priority) for map centering ONLY
            if (globalMapCenter && globalMapCenter.lat && globalMapCenter.lng) {
                const location = { 
                    lat: globalMapCenter.lat, 
                    lng: globalMapCenter.lng 
                };
                setMapCenter(location);
                setMapZoom(10);
                
                // Use cached map center for initial search
                if (!initialSearchDoneRef.current) {
                    initialSearchDoneRef.current = true;
                    searchCampsInMapArea(location, true);
                }
                return;
            }

            // Check if we have a cached physical location second
            if (globalLocation?.coords) {
                const location = { 
                    lat: globalLocation.coords.latitude, 
                    lng: globalLocation.coords.longitude 
                };
                setUserLocation(location);
                setMapCenter(location);
                setMapZoom(10);
                
                // Use cached location for initial search
                if (!initialSearchDoneRef.current) {
                    initialSearchDoneRef.current = true;
                    searchCampsInMapArea(location, true);
                }
                return;
            }

            // No cached data, fetch fresh physical location
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const { latitude, longitude } = position.coords;
                        const location = { lat: latitude, lng: longitude };
                        setUserLocation(location);
                        setMapCenter(location);
                        setMapZoom(10);
                        
                        // Save to global location (physical location) with map center update on first load
                        setGlobalLocation({
                            coords: { latitude, longitude },
                            timestamp: Date.now()
                        }, true); // Update map center on initial load
                        
                        // Search for camps around user location (force initial search)
                        if (!initialSearchDoneRef.current) {
                            initialSearchDoneRef.current = true;
                            searchCampsInMapArea(location, true);
                        }
                    },
                    (error) => {
                        // Fallback to default location and search there (force initial search)
                        if (!initialSearchDoneRef.current) {
                            initialSearchDoneRef.current = true;
                            searchCampsInMapArea(fallbackPosition, true);
                        }
                    },
                    {
                        enableHighAccuracy: true,
                        timeout: 10000,
                        maximumAge: 300000 // 5 minutes
                    }
                );
            } else {
                // No geolocation support, search at fallback position (force initial search)
                if (!initialSearchDoneRef.current) {
                    initialSearchDoneRef.current = true;
                    searchCampsInMapArea(fallbackPosition, true);
                }
            }
        };

        getUserLocation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    const getCategoryIconName = (category?: string): string => {
        if (!category) return 'map-marker';
        const found = CampTypes.find(ct => ct.text.toLowerCase() === String(category).toLowerCase());
        return found?.iconName || 'map-marker';
    };

    const getPopupVerticalOffset = (zoom: number): string => {
        // Adjust vertical position based on zoom level
        // Higher zoom = more space needed at top, so move popup down slightly
        if (zoom >= 12) {
            return '-200px'; // Move down more when zoomed in
        } else if (zoom >= 10) {
            return '-250px'; // Standard position
        } else {
            return '-300px'; // Move up when zoomed out
        }
    };

    const getPopupHorizontalOffset = (zoom: number): string => {
        // Adjust horizontal position to avoid blocking the camp marker
        if (zoom >= 12) {
            return '50px'; // Move significantly to the right when zoomed in
        } else {
            return '30px'; // Move to the right for standard position
        }
    };

    const resetToCurrentLocation = () => {
        setIsLoadingUserLocation(true);

        // Always fetch fresh GPS location when user clicks "My Location" button
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    const location = { lat: latitude, lng: longitude };
                    setUserLocation(location);
                    setMapCenter(location);
                    setMapZoom(12);
                    setSelectedCampId(null);
                    
                    // Save as physical location AND update map center
                    setGlobalLocation({
                        coords: { latitude, longitude },
                        timestamp: Date.now()
                    }, true); // Update map center when going to "my location"

                    // Reset search box location string back to initial (empty) state
                    setGlobalSearch({
                        address: '',
                        startDate: (globalSearch && globalSearch.startDate) ? globalSearch.startDate : null,
                        endDate: (globalSearch && globalSearch.endDate) ? globalSearch.endDate : null,
                        age: (globalSearch && typeof globalSearch.age === 'number') ? globalSearch.age : null,
                        gender: (globalSearch && globalSearch.gender) ? globalSearch.gender : null,
                    });

                    setIsLoadingUserLocation(false);

                    // Search for camps at the actual physical location
                    searchCampsInMapArea(location);
                },
                (error) => {
                    console.error('Error getting current location:', error);
                    alert('Unable to get your current location. Please check location permissions.');
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0 // Force fresh location, don't use cached
                }
            );
        } else {
            alert('Geolocation is not supported by your browser.');
        }
    };

    if (loadError) return <div>Error loading maps</div>;
    if (!isLoaded) return <div>Loading...</div>;

    return (
        <>
            <style>
                {`
                    @keyframes pulse {
                        0% { transform: scale(1); }
                        50% { transform: scale(1.1); }
                        100% { transform: scale(1); }
                    }
                    @keyframes centerPulse {
                        0% { transform: scale(1); opacity: 0.45; }
                        60% { transform: scale(1.8); opacity: 0; }
                        100% { transform: scale(1.8); opacity: 0; }
                    }
                `}
            </style>
            <div style={{display: 'flex', justifyContent: 'center', height: '70vh', width: '100%'}}>
            <div style={{position: 'relative', width: '92%', height: '100%', borderRadius: '20px', overflow: 'hidden'}}>
                <GoogleMap
                    center={mapCenter}
                    zoom={mapZoom}
                    mapContainerStyle={{height: '100%', width: '100%'}}
                    onLoad={setMap}
                    options={
                        {
                            styles: GoogleMapsStyle,
                            disableDefaultUI: true,
                            zoomControl: true
                        }
                    }
                >
                    {/* 30-mile radius circle */}
                    <Circle
                        center={mapCenter}
                        radius={SEARCH_RADIUS_MILES * 1609.34} // Convert miles to meters
                        options={{
                            fillColor: '#4285F4',
                            fillOpacity: 0.08,
                            strokeColor: '#4285F4',
                            strokeOpacity: 0.4,
                            strokeWeight: 2,
                            clickable: false,
                        }}
                    />

                    {/* Map center dot indicator - green pulsing marker */}
                    {mapCenter && (
                        <OverlayView
                            position={mapCenter}
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                            <div
                                style={{
                                    transform: 'translate(-50%, -50%)',
                                    pointerEvents: 'none',
                                    zIndex: 6,
                                    position: 'relative',
                                }}
                            >
                                {/* Pulsing ring */}
                                <div style={{
                                    position: 'absolute',
                                    left: -5,
                                    top: -5,
                                    width: 20,
                                    height: 20,
                                    borderRadius: 12,
                                    border: '2px solid #3C8248',
                                    background: 'transparent',
                                    animation: 'centerPulse 2.2s ease-out infinite',
                                }} />
                                {/* Solid core */}
                                <div style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: 6,
                                    background: '#3C8248',
                                    border: '2px solid #fff',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
                                }} />
                            </div>
                        </OverlayView>
                    )}
                    {/* Current Location Icon - styled like camp category icons */}
                    {(userLocation || fallbackPosition) && (
                        <OverlayView
                            position={userLocation || fallbackPosition}
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                            <div
                                onClick={() => setSelectedCampId(null)}
                                style={{
                                    transform: 'translate(-50%, -115%)',
                                    cursor: 'pointer',
                                    zIndex: 5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexDirection: 'column',
                                    gap: 6,
                                }}
                            >
                                <div style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 18,
                                    background: '#4285F4', // Google Maps blue
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 6px 14px rgba(0,0,0,0.25)',
                                    border: '2px solid #fff',
                                    animation: 'pulse 2s infinite',
                                }}>
                                    <MaterialIcons
                                        name="my-location"
                                        size={20}
                                        color={'#fff'}
                                    />
                                </div>
                                <div style={{
                                    padding: '6px 10px',
                                    background: 'rgba(17,17,17,0.92)',
                                    color: '#fff',
                                    borderRadius: 14,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.25)'
                                }}>
                                    You are here
                                </div>
                            </div>
                        </OverlayView>
                    )}

                    {validCamps.map((camp) => (
                        <React.Fragment key={camp.campId}>
                            {/* Invisible anchor marker for click handling */}
                            <Marker
                                position={{ lat: camp.lat, lng: camp.lng }}
                                onClick={() => setSelectedCampId(camp.campId)}
                                options={{ opacity: 0 }}
                            />

                            {/* Category icon badge */}
                            <OverlayView
                                position={{ lat: camp.lat, lng: camp.lng }}
                                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                            >
                                <div
                                    onClick={() => setSelectedCampId(camp.campId)}
                                    style={{
                                        transform: 'translate(-50%, -115%)',
                                        cursor: 'pointer',
                                        zIndex: selectedCampId === camp.campId ? 10 : 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexDirection: 'column',
                                        gap: 6,
                                    }}
                                >
                                    <div style={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: 18,
                                        background: getCampTypeColor(camp.campCategory),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 6px 14px rgba(0,0,0,0.25)',
                                        border: '2px solid #fff',
                                        transform: selectedCampId === camp.campId ? 'scale(1.12)' : 'scale(1.0)',
                                        transition: 'transform 120ms ease'
                                    }}>
                                        <MaterialIcons
                                            name={getCampTypeIconName(camp.campCategory, 'terrain') as any}
                                            size={20}
                                            color={'#fff'}
                                        />
                                    </div>
                                    {selectedCampId === camp.campId && (
                                        <div style={{
                                            padding: '6px 10px',
                                            background: 'rgba(17,17,17,0.92)',
                                            color: '#fff',
                                            borderRadius: 14,
                                            fontSize: 12,
                                            fontWeight: 600,
                                            whiteSpace: 'nowrap',
                                            boxShadow: '0 4px 10px rgba(0,0,0,0.25)'
                                        }}>
                                            {camp.campCategory || 'Camp'}
                                        </div>
                                    )}
                                </div>
                            </OverlayView>

                            {/* Custom popup overlay */}
                            {selectedCampId === camp.campId && (
                                <OverlayView
                                    position={{ lat: camp.lat, lng: camp.lng }}
                                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                >
                                    <div style={{
                                        position: 'absolute',
                                        left: getPopupHorizontalOffset(mapZoom),
                                        top: getPopupVerticalOffset(mapZoom),
                                        zIndex: 1000,
                                    }}>
                                        <CampMapPopup 
                                            camp={camp} 
                                            onClose={() => setSelectedCampId(null)}
                                        />
                                    </div>
                                </OverlayView>
                            )}
                        </React.Fragment>
                    ))}
                </GoogleMap>
                {/* Reset to Current Location Button - pinned inside map container */}
                <div style={{
                    position: 'absolute',
                    top: 20,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    zIndex: 0,
                    pointerEvents: 'none',
                }}>
                    <Pressable
                        onPress={resetToCurrentLocation}
                        style={{
                            backgroundColor: 'black',
                            borderRadius: 25,
                            paddingHorizontal: 16,
                            paddingVertical: 12,
                            alignItems: 'center',
                            justifyContent: 'center',
                            shadowColor: '#000',
                            shadowOffset: { width: 0, height: 2 },
                            shadowOpacity: 0.25,
                            shadowRadius: 4,
                            elevation: 5,
                            borderWidth: 1,
                            borderColor: '#333',
                            flexDirection: 'row',
                            gap: 8,
                            pointerEvents: 'auto',
                        }}
                    >
                        <MaterialIcons
                            name="my-location"
                            size={20}
                            color="white"
                        />
                        <Text style={{
                            color: 'white',
                            fontSize: 14,
                            fontWeight: '600',
                        }}>
                            {isLoadingUserLocation ? 'Loading Current Location...' : 'My Location'}
                        </Text>
                    </Pressable>
                </div>
            </div>
        </div>
        </>
    );
};

export default CampsMapView;
